import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  NUDGE_STORAGE_KEY,
  NUDGE_VERSIONS,
  type NudgeContext,
  type NudgeId,
  type NudgeState,
  type Nullable,
} from "./nudge.types";

export const NudgeCtx = createContext<Nullable<NudgeContext>>(null);

const EMPTY_STATE: NudgeState = { dismissed: {}, seen: [] };

/** Read + defensively parse persisted nudge state. Never throws. */
function readState(): NudgeState {
  if (typeof window === "undefined") return EMPTY_STATE;
  try {
    const raw = window.localStorage.getItem(NUDGE_STORAGE_KEY);
    if (!raw) return EMPTY_STATE;
    const parsed = JSON.parse(raw);
    return {
      dismissed:
        parsed && typeof parsed.dismissed === "object" && parsed.dismissed
          ? parsed.dismissed
          : {},
      seen: Array.isArray(parsed?.seen) ? parsed.seen : [],
    };
  } catch {
    return EMPTY_STATE;
  }
}

interface Props {
  children: ReactNode;
}

export function NudgeProvider({ children }: Props) {
  // Lazy-init from localStorage so the first paint already has truthy state
  // (this is a CRA SPA — no SSR). Avoids a mount-time wipe/restore race.
  const [state, setState] = useState<NudgeState>(() => readState());

  // Persist on every state change.
  useEffect(() => {
    try {
      window.localStorage.setItem(
        NUDGE_STORAGE_KEY,
        JSON.stringify(state)
      );
    } catch {
      /* ignore quota / private-mode failures */
    }
  }, [state]);

  const isDismissed = useCallback(
    (id: NudgeId) => {
      const dismissedAt = state.dismissed[id];
      if (dismissedAt === undefined) return false;
      const current = NUDGE_VERSIONS[id] ?? 1;
      return dismissedAt >= current;
    },
    [state.dismissed]
  );

  const dismiss = useCallback((id: NudgeId) => {
    setState((prev) => ({
      ...prev,
      dismissed: { ...prev.dismissed, [id]: NUDGE_VERSIONS[id] ?? 1 },
    }));
  }, []);

  const isSeen = useCallback(
    (id: NudgeId) => state.seen.includes(id),
    [state.seen]
  );

  const markSeen = useCallback((id: NudgeId) => {
    setState((prev) =>
      prev.seen.includes(id) ? prev : { ...prev, seen: [...prev.seen, id] }
    );
  }, []);

  const resetAll = useCallback(() => {
    setState(EMPTY_STATE);
  }, []);

  const value = useMemo<NudgeContext>(
    () => ({ isDismissed, dismiss, isSeen, markSeen, resetAll }),
    [isDismissed, dismiss, isSeen, markSeen, resetAll]
  );

  return (
    <NudgeCtx.Provider value={value}>{children}</NudgeCtx.Provider>
  );
}
